/**
 * LiveFeed — Scrollable feed of recent workouts from followed users + gym mates.
 */
import { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, Image, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { supabase } from '@/lib/supabase';
import { fetchLiveFeed, toggleReaction, type FeedItem } from '@/lib/liveFeed';
import { subscribeToFeed } from '@/lib/realtimeSubscriptions';
import { Dumbbell, Trophy, Clock, Users, MapPin } from 'lucide-react-native';
import FollowSuggestions from './FollowSuggestions';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

export default function LiveFeed() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { profile } = useAuth();
  const router = useRouter();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reactedPosts, setReactedPosts] = useState<Set<string>>(new Set());

  const loadFeed = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    const feed = await fetchLiveFeed({
      userId: profile.id,
      gymId: profile.home_gym_id || profile.gym_id,
    });
    setItems(feed);
    setLoading(false);
  }, [profile?.id, profile?.home_gym_id, profile?.gym_id]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  // Realtime: prepend new posts as they arrive
  useEffect(() => {
    const gymId = profile?.home_gym_id || profile?.gym_id || null;
    const channel = subscribeToFeed(gymId, (payload: any) => {
      if (payload.eventType === 'INSERT' && payload.new) {
        // Re-fetch to get full joined data rather than raw row
        loadFeed();
      }
    });
    return () => { supabase.removeChannel(channel); };
  }, [profile?.home_gym_id, profile?.gym_id, loadFeed]);

  const loadMore = async () => {
    if (loadingMore || items.length === 0 || !profile?.id) return;
    setLoadingMore(true);
    const lastItem = items[items.length - 1];
    const more = await fetchLiveFeed({
      userId: profile.id,
      gymId: profile.home_gym_id || profile.gym_id,
      before: lastItem.createdAt,
    });
    setItems(prev => [...prev, ...more]);
    setLoadingMore(false);
  };

  const handleReaction = async (postId: string) => {
    if (!profile?.id) return;
    const result = await toggleReaction(postId, profile.id);
    setItems(prev => prev.map(i => i.id === postId ? { ...i, likeCount: result.count } : i));
    setReactedPosts(prev => {
      const next = new Set(prev);
      result.reacted ? next.add(postId) : next.delete(postId);
      return next;
    });
  };

  const renderItem = ({ item }: { item: FeedItem }) => {
    const isPR = item.postType === 'pr';
    const icon = isPR ? Trophy : Dumbbell;
    const IconComp = icon;

    return (
      <TouchableOpacity
        style={[styles.card, { backgroundColor: c.surface, borderColor: isPR ? c.accent + '40' : c.border }]}
        onPress={() => router.push(`/u/${item.handle}` as any)}
        activeOpacity={0.7}
      >
        <View style={styles.cardHeader}>
          {item.avatarUrl ? (
            <Image source={{ uri: item.avatarUrl }} style={[styles.avatar, { borderColor: c.border }]} />
          ) : (
            <View style={[styles.avatar, styles.avatarPlaceholder, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}>
              <Text style={[styles.avatarInitial, { color: c.textSecondary }]}>
                {(item.displayName || '?')[0].toUpperCase()}
              </Text>
            </View>
          )}
          <View style={{ flex: 1 }}>
            <Text style={[styles.name, { color: c.text, fontFamily: TYPO.body.semibold }]}>
              {item.displayName}
            </Text>
            <View style={styles.metaRow}>
              <Clock size={11} color={c.textTertiary} />
              <Text style={[styles.time, { color: c.textTertiary }]}>{timeAgo(item.createdAt)}</Text>
            </View>
          </View>
          <View style={[styles.typePill, { backgroundColor: isPR ? c.accent + '20' : c.surfaceVariant }]}>
            <IconComp size={12} color={isPR ? c.accent : c.textSecondary} />
            <Text style={[styles.typeText, { color: isPR ? c.accent : c.textSecondary, fontFamily: TYPO.body.semibold }]}>
              {isPR ? 'PR' : 'WORKOUT'}
            </Text>
          </View>
        </View>
        <Text style={[styles.content, { color: c.text }]} numberOfLines={3}>
          {item.caption || item.content}
        </Text>
        <TouchableOpacity
          style={styles.reactionRow}
          onPress={() => handleReaction(item.id)}
          activeOpacity={0.6}
        >
          <Text style={[styles.reactionEmoji, reactedPosts.has(item.id) && styles.reactionActive]}>
            🔥
          </Text>
          {item.likeCount > 0 && (
            <Text style={[styles.reactionCount, { color: reactedPosts.has(item.id) ? c.accent : c.textTertiary }]}>
              {item.likeCount}
            </Text>
          )}
        </TouchableOpacity>
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator color={c.accent} />
      </View>
    );
  }

  if (items.length === 0) {
    const hasGym = !!(profile?.home_gym_id || profile?.gym_id);
    return (
      <View style={styles.emptyContainer}>
        <FollowSuggestions />
        <View style={[styles.empty, { borderColor: c.border }]}>
          <Users size={24} color={c.textTertiary} />
          <Text style={[styles.emptyTitle, { color: c.textSecondary, fontFamily: TYPO.heading.bold }]}>
            NO ACTIVITY YET
          </Text>
          <Text style={[styles.emptyDesc, { color: c.textTertiary }]}>
            Follow lifters to see their workouts here
          </Text>
          {!hasGym && (
            <TouchableOpacity
              style={[styles.gymCta, { backgroundColor: c.accent }]}
              onPress={() => router.push('/gym' as any)}
              activeOpacity={0.7}
            >
              <MapPin size={14} color="#fff" />
              <Text style={[styles.gymCtaText, { fontFamily: TYPO.body.semibold }]}>JOIN A GYM</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }

  return (
    <FlatList
      data={items}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      contentContainerStyle={styles.list}
      onEndReached={loadMore}
      onEndReachedThreshold={0.3}
      ListFooterComponent={loadingMore ? <ActivityIndicator color={c.accent} style={{ padding: 16 }} /> : null}
    />
  );
}

const styles = StyleSheet.create({
  list: { gap: SPACE.sm, paddingBottom: 20 },
  loadingContainer: { padding: 40, alignItems: 'center' },
  card: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 14, gap: SPACE.sm },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm },
  avatar: { width: 36, height: 36, borderRadius: 18, borderWidth: 1 },
  avatarPlaceholder: { alignItems: 'center', justifyContent: 'center' },
  avatarInitial: { fontSize: 14, fontWeight: '700' },
  name: { fontSize: FONT.sm },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 3, marginTop: 1 },
  time: { fontSize: 11 },
  typePill: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 3, paddingHorizontal: 8, borderRadius: RADIUS.xs },
  typeText: { fontSize: 11, letterSpacing: 0.5 },
  content: { fontSize: FONT.sm, lineHeight: 20 },
  reactionRow: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingTop: 2 },
  reactionEmoji: { fontSize: 16, opacity: 0.5 },
  reactionActive: { opacity: 1 },
  reactionCount: { fontSize: 12 },
  emptyContainer: { gap: SPACE.md },
  empty: { padding: 32, alignItems: 'center', gap: SPACE.sm, borderWidth: 1, borderRadius: RADIUS.xs, borderStyle: 'dashed' },
  emptyTitle: { fontSize: 13, letterSpacing: 1 },
  emptyDesc: { fontSize: 12, textAlign: 'center', lineHeight: 18 },
  gymCta: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8, paddingHorizontal: 16, borderRadius: RADIUS.xs, marginTop: 4 },
  gymCtaText: { color: '#fff', fontSize: 12, letterSpacing: 0.5 },
});
